import type { ChatChannelName } from "../../../../channels/types";
import type { ToTarget } from "../../../../to/to-target";
import type {
  GenericToolbarDropdownElement,
  GenericToolbarElement,
  NodeTypeToolbarElements,
} from "../../plugins/generic-toolbar/types";

export function chatToolbarElements(
  channel: ChatChannelName,
  toTarget: ToTarget["toTarget"],
  config?: {
    blacklistedElements?: (GenericToolbarElement["type"] | GenericToolbarDropdownElement["type"])[];
  },
): {
  topLevel: GenericToolbarElement[];
  floating: NodeTypeToolbarElements;
} {
  const blacklistedElements = config?.blacklistedElements ?? [];

  let result: {
    topLevel: GenericToolbarElement[];
    floating: NodeTypeToolbarElements;
  };

  switch (channel) {
    case "slack":
      result = {
        topLevel: [
          { type: "bold" } as const,
          { type: "italic" } as const,
          { type: "mergeTagPicker" } as const,
          { type: "listMenu" } as const,
          { type: "linkButton" } as const,
          { type: "emojiPicker" } as const,
          {
            type: "toolbarDropDown" as const,
            children: [
              { type: "addImagesAndFiles" } as const,
              { type: "imageButton" } as const,
              { type: "addButton", channel: "slack" } as const,
              ...(toTarget !== "channel" ? [{ type: "dynamicContent" } as const] : []),
              { type: "addDivider", channel: "slack" } as const,
              { type: "surveyAnswersButton" } as const,
            ],
          },
          { type: "pollButton" } as const,
          { type: "surveyButton", channel: "slack" } as const,
        ],
        floating: {
          text: [
            { type: "bold" } as const,
            { type: "italic" } as const,
            { type: "linkButton" } as const,
          ],
          savedImage: [{ type: "deleteNodeButton" } as const],
          unsavedImage: [
            { type: "saveImageButton" } as const,
            { type: "deleteNodeButton" } as const,
          ],
          button: null,
          unsavedDesignHuddle: [
            { type: "saveImageButton" } as const,
            { type: "deleteNodeButton" } as const,
            { type: "imageFullWidthButton" } as const,
            { type: "editButton" } as const,
          ],
          savedDesignHuddle: [
            { type: "deleteNodeButton" } as const,
            { type: "imageFullWidthButton" } as const,
            { type: "editButton" } as const,
            { type: "videoLinkButton" } as const,
          ],
          poll: [{ type: "editButton" } as const, { type: "deleteNodeButton" } as const],
          videoAsset: [{ type: "deleteNodeButton" } as const, { type: "videoLinkButton" } as const],
        },
      };
      break;

    case "teams":
      result = {
        topLevel: [
          { type: "bold" } as const,
          { type: "italic" } as const,
          { type: "listMenu" } as const,
          { type: "mergeTagPicker" } as const,
          { type: "linkButton" } as const,
          { type: "emojiPicker" } as const,
          {
            type: "toolbarDropDown" as const,
            children: [
              { type: "addImagesAndFiles" } as const,
              { type: "imageButton" } as const,
              { type: "addButton", channel: "teams" } as const,
              ...(toTarget !== "channel" ? [{ type: "dynamicContent" } as const] : []),
              { type: "addDivider", channel: "teams" } as const,
              { type: "surveyAnswersButton" } as const,
            ],
          },
          { type: "pollButton" } as const,
          { type: "surveyButton", channel: "teams" } as const,
        ],
        floating: {
          text: [
            { type: "bold" } as const,
            { type: "italic" } as const,
            { type: "linkButton" } as const,
          ],
          unsavedImage: [
            { type: "saveImageButton" } as const,
            { type: "deleteNodeButton" } as const,
            { type: "imageFullWidthButton" } as const,
          ],
          savedImage: [
            { type: "deleteNodeButton" } as const,
            { type: "imageFullWidthButton" } as const,
          ],
          button: null,
          unsavedDesignHuddle: [
            { type: "saveImageButton" } as const,
            { type: "deleteNodeButton" } as const,
            { type: "imageFullWidthButton" } as const,
            { type: "editButton" } as const,
          ],
          savedDesignHuddle: [
            { type: "deleteNodeButton" } as const,
            { type: "imageFullWidthButton" } as const,
            { type: "editButton" } as const,
            { type: "videoLinkButton" } as const,
          ],
          poll: [{ type: "editButton" } as const, { type: "deleteNodeButton" } as const],
          videoAsset: [{ type: "deleteNodeButton" } as const, { type: "videoLinkButton" } as const],
        },
      };
      break;
  }

  // Remove any blacklisted elements from topLevel and dropdowns
  result.topLevel = result.topLevel
    .filter((el) => el.type !== "toolbarDropDown" || el.children?.length)
    .map((el) => {
      if (el.type === "toolbarDropDown") {
        return {
          ...el,
          children: el.children.filter((c) => !blacklistedElements.includes(c.type)),
        };
      }
      return el;
    })
    .filter((el) => !blacklistedElements.includes(el.type));

  // Filter floating items as well
  for (const key in result.floating) {
    const val = result.floating[key as keyof NodeTypeToolbarElements];
    if (Array.isArray(val)) {
      result.floating[key as keyof NodeTypeToolbarElements] = val.filter(
        (el) => !blacklistedElements.includes(el.type),
      );
    }
  }

  return result;
}
